import axios, { AxiosInstance, AxiosResponse, CancelToken } from 'axios';
import { getApiConfig } from '../environment';
import { formatPodDate } from './podDate';

// Clerk type declaration
declare global {
    interface Window {
        Clerk?: {
            session?: {
                getToken?: ({ template }: { template: string }) => Promise<string | null>;
            };
        };
    }
}

const apiConfig = getApiConfig();

const axiosInstance: AxiosInstance = axios.create({
  baseURL: apiConfig.apiBaseUrl,
});

export interface DetailedResponse {
    status: string;
}

export interface ItemConversion {
    itemNumber: string;
    conversionNumber: number;
    isFrozen?: boolean;
}

export interface FranchiseStore {
    storeNumber: string,
    starbucksStoreNumber: string
}

export interface PodConfigurationModel {
    id: number,
    storeNumber: string,
    storeName: string,
    starbucksStoreNumber: string,
    itemNumber: string,
    itemDescription: string,
    franchiseName: string,
    minStockOnHandQuantity: number | string | null,
    maxStockOnHandQuantity: number | string | null,
    maxOrderQuantity: number | string | null,
    safetyStock: number | string | null,
    isParEnabled: boolean,
    configurationLevel: number,
    enabled: boolean,
    franchiseStores: FranchiseStore[],
    subscriptionId?: number | null,
    subscriptionOrderTypeId?: number | null,
    subscriptionCategoryId?: number | null
}

export interface PredictedDemandModel {
    itemNumber: string,
    predictionInfo: PredictionInfo | null,
    predictedDemandItems: PredictedDemandItemModel[]
}

export interface PredictedDemandItemModel {
    quantity: number,
    date: Date,
    hasPrediction: boolean,
    predictedDemandQuantity: number,
    safetyStock: number | null,
    minStockOnHandQty?: number | null
}

export interface PredictionInfo {
}

export interface StoreConfiguration {
    isPodEnabled: boolean
}

export interface CategorySubscriptionModel {
    id?: number,
    franchiseName: string,
    orderTypeId: number,
    categoryId: number | null,
    subscribed: boolean
}

export interface BulkConfigurationSaveModel {
    franchiseName: string,
    subscriptionChanges: CategorySubscriptionModel[],
    configurationsToCreate: PodConfigurationModel[],
    configurationsToUpdate: PodConfigurationModel[]
}


class Client {

  private static instance: Client;
  private readonly axiosInstance: AxiosInstance;

  private constructor(axiosInstance: AxiosInstance) {
    this.axiosInstance = axiosInstance;

    this.axiosInstance.interceptors.request.use(async (config) => {

      const token = await window.Clerk?.session?.getToken?.({ template: 'Orderly_default' });
      if (!token) {
        throw new Error('Authorization token is missing.');
      }

      config.headers['Accept'] = 'application/json';
      config.headers['Authorization'] = `Bearer ${token}`;
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      config.headers['Expires'] = '-1';
      config.headers['If-Modified-Since'] = 'Thu, 13 Feb 1985 13:40:01 GMT';
      config.headers['Cache-Control'] = 'no-cache';
      config.headers['Pragma'] = 'no-cache';

      return config;
    }, (error) => {
      return Promise.reject(error);
    });
  }

  public static getInstance(): Client {
    if (!Client.instance) {
      Client.instance = new Client(axiosInstance);
    }
    return Client.instance;
  }


  public async getConfigurations(configurationLevel: number, franchiseName?: string, storeNumber?: string, cancelToken?: CancelToken): Promise<PodConfigurationModel[]> {
    try {
      const params = {
        configurationLevel,
        franchiseName,
        storeNumber,
      };

      const response: AxiosResponse<PodConfigurationModel[]> = await this.axiosInstance.get('getconfigurations', { params, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getStoreScopedConfigurations(
    configurationLevel: number,
    franchiseName: string,
    storeNumber: string,
    cancelToken?: CancelToken,
  ): Promise<PodConfigurationModel[]> {
    try {
      const params = {
        configurationLevel,
        franchiseName,
        storeNumber,
      };
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<PodConfigurationModel[]> = await this.axiosInstance.get(
        'getstoreconfigurations',
        { params, headers, cancelToken },
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getStoreConfiguration(franchiseName: string, storeNumber: string, cancelToken?: CancelToken): Promise<StoreConfiguration> {
    try {
      const params = {
        franchiseName,
        storeNumber,
      };

      const response: AxiosResponse<StoreConfiguration> = await this.axiosInstance.get('getstoreconfiguration', { params, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getPredictions(franchiseName: string, storeNumber: string, availableDates: string[], cancelToken?: CancelToken): Promise<PredictedDemandModel[]> {
    try {
      const dates = availableDates.map(formatPodDate).join(',');
      const params = {
        franchiseName,
        storeNumber,
        dates,
      };

      const response: AxiosResponse<PredictedDemandModel[]> = await this.axiosInstance.get('getpredictions', { params, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }
  public async getPredictionsForItems(franchiseName: string, storeNumber: string, deliveryDates: string[], itemNumbersList: string[], conversions?: ItemConversion[], cancelToken?: CancelToken): Promise<PredictedDemandModel[]> {
    try {
      const dates = deliveryDates.map(formatPodDate);

      const requestBody = {
        franchiseName,
        storeNumber,
        dates,
        itemNumbers: itemNumbersList,
        conversions: conversions || [],
      };

      const response: AxiosResponse<PredictedDemandModel[]> = await this.axiosInstance.post('getpredictionsforitems', requestBody, { cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async create(model: PodConfigurationModel, cancelToken?: CancelToken): Promise<DetailedResponse> {
    try {
      const response: AxiosResponse = await this.axiosInstance.post('createconfiguration', model, { cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async update(model: PodConfigurationModel, cancelToken?: CancelToken): Promise<DetailedResponse> {
    try {
      const response: AxiosResponse = await this.axiosInstance.put('updateconfiguration', model, { cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }


  public async delete(id: number, cancelToken?: CancelToken): Promise<DetailedResponse> {
    try {
      const response: AxiosResponse = await this.axiosInstance.delete(`deleteconfiguration/${id}`, { cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  /** Creates a batch of POD configurations. */
  public async createBatch(configs: PodConfigurationModel[], cancelToken?: CancelToken): Promise<DetailedResponse> {
    try {
      const response: AxiosResponse = await this.axiosInstance.post('createconfiguration', configs, { cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  /** Updates a batch of POD configurations. */
  public async updateBatch(configs: PodConfigurationModel[], cancelToken?: CancelToken): Promise<DetailedResponse> {
    try {
      const response: AxiosResponse = await this.axiosInstance.put('updateconfiguration', configs, { cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async updateStoreScopedBatch(
    configs: PodConfigurationModel[],
    storeNumber: string,
    cancelToken?: CancelToken,
  ): Promise<DetailedResponse> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse = await this.axiosInstance.put(
        'updatestoreconfiguration',
        configs,
        { headers, cancelToken },
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  /** Gets a franchise's AI ordering category/order-type subscriptions (used by nightly auto-enable). */
  public async getCategorySubscriptions(franchiseName: string, cancelToken?: CancelToken): Promise<CategorySubscriptionModel[]> {
    try {
      const params = { franchiseName };
      const response: AxiosResponse<CategorySubscriptionModel[]> = await this.axiosInstance.get('getcategorysubscriptions', { params, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  /**
   * Persists whether a whole order type (categoryId null) or a single category is subscribed to
   * AI ordering. Newly-introduced OM items under a subscribed node get auto-enabled by POD's
   * nightly reconciliation job, instead of requiring a manual re-run of the bulk toggle. Returns
   * the upserted subscription (including its Id) so callers can stamp it onto config rows they
   * create in the same save.
   */
  public async setCategorySubscription(model: CategorySubscriptionModel, cancelToken?: CancelToken): Promise<CategorySubscriptionModel> {
    try {
      const response: AxiosResponse<CategorySubscriptionModel> = await this.axiosInstance.post('setcategorysubscription', model, { cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async saveBulkConfiguration(model: BulkConfigurationSaveModel, cancelToken?: CancelToken): Promise<CategorySubscriptionModel[]> {
    try {
      const response: AxiosResponse<CategorySubscriptionModel[]> = await this.axiosInstance.post('savebulkconfiguration', model, { cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

}


export const podClient = Client.getInstance();